import type { PersonDto } from '@betreuung/shared';
import { Trash2 } from 'lucide-react';
import { Swatch } from '@/components/shared/Swatch';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { de } from '@/i18n/de';

export interface PersonListProps {
  persons: PersonDto[];
  onEdit: (person: PersonDto) => void;
  onDelete: (person: PersonDto) => void;
}

export function PersonList({ persons, onEdit, onDelete }: PersonListProps) {
  return (
    <ul>
      {persons.map((person) => {
        // A person that still owns time cannot be removed; the server refuses it
        // as well, the disabled icon just says so before the attempt.
        const deletable = person.assignmentCount === 0;

        return (
          <li
            key={person.id}
            className="flex items-center gap-2.5 border-b border-divider px-4 py-3"
          >
            <Swatch colorIndex={person.colorIndex} size={26} radius={8} />

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-row font-semibold text-fg">{person.name}</span>
                <Badge>
                  {person.hasAccount ? de.admin.badgeAccount : de.admin.badgeNoAccount}
                </Badge>
                {/* Without this a view-only account looks exactly like a
                    Betreuungsperson with a login, and the difference is the
                    whole point of the role. */}
                {person.isCaregiver ? null : (
                  <Badge variant="solid">{de.admin.badgeViewOnly}</Badge>
                )}
                {person.isAdmin ? <Badge variant="solid">{de.admin.badgeAdmin}</Badge> : null}
              </div>
              <p className="pt-0.5 text-duration text-fg-hint">
                {de.admin.assignmentCount(person.assignmentCount)}
              </p>
            </div>

            <button
              type="button"
              aria-label={de.admin.editPerson(person.name)}
              onClick={() => onEdit(person)}
              className="hit-44 shrink-0 rounded-cell px-1 text-label text-fg-hint hover:text-fg"
            >
              {de.admin.edit}
            </button>

            <button
              type="button"
              disabled={!deletable}
              aria-label={de.admin.deletePerson(person.name)}
              onClick={() => onDelete(person)}
              className={cn(
                'hit-44 flex shrink-0 items-center justify-center',
                deletable ? 'text-cal-9' : 'text-fg-disabled',
              )}
            >
              <Trash2 className="size-4" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
